import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import CustomModal from '../Modal/CustomModal';
import Loader from '../Loader';
import Message from '../Message';
import { customSwal } from '../CustomSwal';
import {
  ACCEPTED_IMAGE_TYPES,
  deleteConsoleMedia,
  formatBytes,
  listConsoleMedia,
  uploadConsoleImage
} from './consoleMediaApi';

const smallButton = 'rounded px-2 py-1 text-xs font-semibold';

// A console's uploaded images. Pass onSelect to use it as a picker (e.g. for image items).
const ConsoleMediaLibrary = ({ isOpen, onClose, consoleId, onSelect }) => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAssets(await listConsoleMedia(consoleId, token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [consoleId, token]);

  useEffect(() => {
    if (isOpen && consoleId) {
      loadAssets();
    }
  }, [isOpen, consoleId, loadAssets]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    setUploading(true);
    const failures = [];
    for (const file of files) {
      try {
        await uploadConsoleImage(file, { consoleId }, token);
      } catch (err) {
        failures.push(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);

    if (failures.length) {
      customSwal.fire({ icon: 'error', title: 'Some uploads failed', text: failures.join('\n') });
    }
    loadAssets();
  };

  const handleDelete = async (asset) => {
    const { isConfirmed } = await customSwal.fire({
      title: 'Delete this image?',
      text: 'It will be permanently removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!isConfirmed) return;

    try {
      await deleteConsoleMedia(asset._id, token);
      setAssets((prev) => prev.filter((a) => a._id !== asset._id));
    } catch (err) {
      customSwal.fire({ icon: 'error', title: "Couldn't delete image", text: err.message });
    }
  };

  const handleCopy = async (asset) => {
    try {
      await navigator.clipboard.writeText(asset.url);
      setCopiedId(asset._id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      customSwal.fire({ icon: 'info', title: 'Image URL', text: asset.url });
    }
  };

  const unusedCount = assets.filter((asset) => asset.usedBy.length === 0).length;
  const totalBytes = assets.reduce((sum, asset) => sum + (asset.bytes || 0), 0);
  const visibleAssets = showUnusedOnly ? assets.filter((asset) => asset.usedBy.length === 0) : assets;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={onSelect ? 'Choose an image' : 'Media library'}
      size="2xl"
    >
      <div className="space-y-4 text-left">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-300">
            {assets.length} {assets.length === 1 ? 'image' : 'images'} · {formatBytes(totalBytes)}
            {unusedCount > 0 && ` · ${unusedCount} unused`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={showUnusedOnly}
                onChange={(e) => setShowUnusedOnly(e.target.checked)}
              />
              Unused only
            </label>
            <label
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white ${
                uploading ? 'cursor-wait bg-blue-600/50' : 'cursor-pointer bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <FontAwesomeIcon icon={faUpload} />
              {uploading ? 'Uploading…' : 'Upload'}
              <input
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                multiple
                hidden
                disabled={uploading}
                onChange={handleUpload}
              />
            </label>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error}</Message>
        ) : visibleAssets.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">
            {assets.length === 0 ? 'No images uploaded yet.' : 'Every image is in use.'}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {visibleAssets.map((asset) => {
              const usedBy = asset.usedBy.map((item) => item.displayName).join(', ');

              return (
                <div key={asset._id} className="overflow-hidden rounded-lg border border-gray-700 bg-gray-900">
                  <button
                    type="button"
                    onClick={() => (onSelect ? onSelect(asset) : window.open(asset.url, '_blank', 'noopener,noreferrer'))}
                    title={onSelect ? 'Use this image' : 'Open full size'}
                    className="block aspect-square w-full bg-black/40"
                  >
                    <img src={asset.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                  <div className="space-y-1.5 p-2 text-xs">
                    <div className="flex items-center justify-between text-gray-400">
                      <span>{asset.width && asset.height ? `${asset.width}×${asset.height}` : ''}</span>
                      <span>{formatBytes(asset.bytes)}</span>
                    </div>
                    {usedBy ? (
                      <p className="truncate text-green-300" title={usedBy}>Used in {usedBy}</p>
                    ) : (
                      <p className="text-yellow-300">Unused</p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {onSelect && (
                        <button
                          type="button"
                          onClick={() => onSelect(asset)}
                          className={`${smallButton} bg-blue-600 text-white hover:bg-blue-700`}
                        >
                          Use
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopy(asset)}
                        className={`${smallButton} bg-gray-700 text-gray-200 hover:bg-gray-600`}
                      >
                        {copiedId === asset._id ? 'Copied' : 'Copy URL'}
                      </button>
                      {!usedBy && (
                        <button
                          type="button"
                          onClick={() => handleDelete(asset)}
                          className={`${smallButton} bg-gray-700 text-gray-200 hover:bg-red-600 hover:text-white`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-400">
          Images are deleted automatically when they're removed from an item. Unused images here can be deleted by hand.
        </p>
      </div>
    </CustomModal>
  );
};

export default ConsoleMediaLibrary;
